// Council picker map: England and Wales local authority districts, click to select (as
// xenscope's LADMap in 99-portal/src/components/LADMap.tsx, which this follows).
//
// Differences from xenscope's: no basemap tiles (CARTO's tiles need a commercial licence;
// the ONS boundaries alone are enough to pick councils, and keep us on open data), colours
// from the design tokens (navy selection, lime hover edge), and a read-only mode once the
// company's councils are locked.
//
// Boundaries: public/maps/councils.geojson (ONS LAD May 2026 BUC, OGL v3.0), properties
// {code, name}; codes match common/data/councils.json in the API. Scotland and Northern
// Ireland (public/maps/uk-context.geojson, same source) are drawn greyed out underneath and
// can't be picked: DECs and EPCs only cover England and Wales.
import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

interface Props {
  selected: string[];
  limit: number;
  readOnly?: boolean;
  onChange: (codes: string[]) => void;
}

/** CSS custom property value (so the map follows the light/dark theme). */
function token(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export function CouncilMap({ selected, limit, readOnly = false, onChange }: Props) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layers = useRef<Map<string, L.Path>>(new Map());
  const names = useRef<Map<string, string>>(new Map());
  // Latest props for the Leaflet event handlers (bound once)
  const state = useRef({ selected, limit, readOnly, onChange });
  state.current = { selected, limit, readOnly, onChange };
  const [notice, setNotice] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // One line weight for every boundary (selected, hover, the rest, Scotland and NI), so
  // shared edges look the same everywhere; selected districts are brought to the front
  // so their navy edge is drawn last and never half-covered by a neighbour's grey one
  const LINE = 1;
  const styleFor = (code: string, hover = false): L.PathOptions => {
    const on = state.current.selected.includes(code);
    return {
      weight: LINE,
      color: on ? token("--brand-primary", "#202937") : hover ? token("--brand-text", "#202937") : token("--brand-text-tertiary", "#8e959e"),
      fillColor: token("--brand-primary", "#202937"),
      fillOpacity: on ? 0.55 : hover ? 0.18 : 0.06,
    };
  };

  // Build the map once
  useEffect(() => {
    if (!el.current || map.current) return;
    const m = L.map(el.current, { zoomControl: true, attributionControl: true, minZoom: 5, maxZoom: 11, zoomSnap: 0.25 }).setView([52.6, -1.6], 6);
    m.attributionControl.setPrefix(false);
    m.attributionControl.addAttribution("Boundaries: ONS, OGL v3.0. Contains OS data © Crown copyright 2026");
    map.current = m;
    // React StrictMode (dev) mounts, unmounts and remounts: ignore a torn-down run's results
    let cancelled = false;

    // Scotland and Northern Ireland: greyed out, not interactive, drawn first so they sit underneath
    const context = fetch("/maps/uk-context.geojson")
      .then((r) => r.json())
      .then((data) =>
        L.geoJSON(data, {
          interactive: false,
          style: () => ({ weight: LINE, color: token("--brand-border-strong", "#9a9a9a"), fillColor: token("--brand-border", "#e5e4e2"), fillOpacity: 0.7 }),
        }).addTo(m),
      )
      .catch(() => null);

    Promise.all([context, fetch("/maps/councils.geojson").then((r) => r.json())])
      .then(([contextLayer, data]) => {
        if (cancelled) return;
        const geo = L.geoJSON(data, {
          style: (f) => styleFor(f?.properties.code),
          onEachFeature: (feature, layer) => {
            const { code, name } = feature.properties as { code: string; name: string };
            const path = layer as L.Path;
            layers.current.set(code, path);
            names.current.set(code, name);
            path.bindTooltip(name, { sticky: true, direction: "top", className: "council-tip" });
            path.on("mouseover", () => {
              path.setStyle(styleFor(code, true));
              path.bringToFront();
            });
            path.on("mouseout", () => {
              path.setStyle(styleFor(code));
              // Back under the selected districts once the pointer leaves
              if (!state.current.selected.includes(code)) path.bringToBack();
            });
            path.on("click", () => {
              const { selected: cur, limit: max, readOnly: ro, onChange: change } = state.current;
              if (ro) return;
              if (cur.includes(code)) {
                change(cur.filter((c) => c !== code));
              } else if (cur.length >= max) {
                setNotice(`Your plan covers up to ${max} Local Authority Districts. Remove one first.`);
                window.setTimeout(() => setNotice(null), 2500);
              } else {
                change([...cur, code]);
              }
            });
          },
        }).addTo(m);
        // Open on England and Wales (centred on the Midlands, every coast in view); panning
        // may reach Scotland and Northern Ireland
        const uk = contextLayer ? geo.getBounds().extend(contextLayer.getBounds()) : geo.getBounds();
        m.setMaxBounds(uk.pad(0.2));
        // Saved coverage: zoom to the chosen districts with a little margin. Otherwise
        // the whole of England and Wales. (Not re-fitted while clicking, to avoid jumps.)
        const chosen = state.current.selected.map((c) => layers.current.get(c)).filter(Boolean) as L.Path[];
        if (chosen.length) m.fitBounds(L.featureGroup(chosen).getBounds().pad(0.25), { maxZoom: 9 });
        else m.fitBounds(geo.getBounds(), { padding: [6, 6] });
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setNotice("The map couldn't load. Use the search box instead.");
      });

    return () => {
      cancelled = true;
      m.remove();
      map.current = null;
      layers.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restyle when the selection changes; selected districts on top (see LINE)
  useEffect(() => {
    layers.current.forEach((path, code) => path.setStyle(styleFor(code)));
    selected.forEach((code) => layers.current.get(code)?.bringToFront());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, loaded]);

  return (
    <div className={`council-map${readOnly ? " is-locked" : ""}`}>
      <div ref={el} className="council-map-canvas" role="application" aria-label="Map of Local Authority Districts in England and Wales" />
      {notice && <div className="council-map-notice">{notice}</div>}
      {!readOnly && <p className="council-map-hint">Click districts on the map, or search below. {selected.length} of {limit} chosen.</p>}
    </div>
  );
}
